import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { AdminUserGrowthData } from '../../../types/admin';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

interface UserGrowthChartProps {
  data: AdminUserGrowthData[];
  loading?: boolean;
}

/**
 * UserGrowthChart - Line chart showing user growth over time
 *
 * Displays:
 * - Total users over last 30 days
 * - New user signups per day
 * - Growth trend indicator
 *
 * Features:
 * - Interactive tooltips
 * - Responsive chart sizing
 * - Smooth curves for better visualization
 * - Color-coded lines (total users: blue, new users: green)
 * - Loading state with skeleton
 */
export default function UserGrowthChart({ data, loading }: UserGrowthChartProps) {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            User Growth
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Calculate growth percentage
  const firstDay = data[0];
  const lastDay = data[data.length - 1];
  const growthPercentage = firstDay.users > 0
    ? ((lastDay.users - firstDay.users) / firstDay.users * 100).toFixed(1)
    : '0';

  // Prepare data for chart
  const dates = data.map(d => {
    const date = new Date(d.date);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  });
  const totalUsers = data.map(d => d.users);
  const newUsers = data.map(d => d.newUsers);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            User Growth
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendingUpIcon color="success" fontSize="small" />
            <Typography variant="body2" color="success.main">
              +{growthPercentage}% (30 days)
            </Typography>
          </Box>
        </Box>

        <LineChart
          height={300}
          series={[
            {
              data: totalUsers,
              label: 'Total Users',
              color: '#1976d2',
              curve: 'natural',
              showMark: false,
            },
            {
              data: newUsers,
              label: 'New Users/Day',
              color: '#2e7d32',
              curve: 'natural',
              showMark: false,
            },
          ]}
          xAxis={[
            {
              scaleType: 'point',
              data: dates,
              tickLabelStyle: {
                fontSize: 11,
              },
            },
          ]}
          yAxis={[
            {
              tickLabelStyle: {
                fontSize: 11,
              },
            },
          ]}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'right' },
              padding: 0,
              itemMarkWidth: 10,
              itemMarkHeight: 10,
              markGap: 5,
              itemGap: 15,
              labelStyle: {
                fontSize: 12,
              },
            },
          }}
          margin={{ top: 50, right: 20, bottom: 30, left: 50 }}
          sx={{
            '& .MuiLineElement-root': {
              strokeWidth: 2,
            },
            '& .MuiChartsAxis-tickLabel': {
              fill: 'text.secondary',
            },
            '& .MuiChartsLegend-series text': {
              fill: 'text.primary !important',
            },
          }}
        />
      </CardContent>
    </Card>
  );
}
